import { apiRequest } from './axiosConfig';
import {
  GoogleAdsCandidate,
  GoogleAdsChangeInput,
  GoogleAdsChangeRequest,
  GoogleAdsConnection,
  GoogleAdsPending,
  GoogleAdsDashboard,
  GoogleAdsDatePreset,
  GoogleAdsReportLevel,
} from '../../types/googleAds';

interface Envelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

class GoogleAdsService {
  async getConnection(): Promise<GoogleAdsConnection> {
    const response = await apiRequest.get<Envelope<GoogleAdsConnection>>('/google-ads/connection');
    return response.data;
  }

  async initiateOAuth(): Promise<string> {
    const response = await apiRequest.post<Envelope<{ authorize_url: string }>>('/google-ads/oauth/initiate');
    return response.data.authorize_url;
  }

  async getCandidates(state: string): Promise<GoogleAdsCandidate[]> {
    const response = await apiRequest.get<Envelope<{ candidates: GoogleAdsCandidate[] }>>(
      '/google-ads/oauth/candidates',
      { params: { state } },
    );
    return response.data.candidates;
  }

  async getPending(state?: string): Promise<GoogleAdsPending | null> {
    const response = await apiRequest.get<Envelope<{ pending: GoogleAdsPending | null }>>(
      '/google-ads/oauth/pending',
      { params: state ? { state } : undefined },
    );
    return response.data.pending;
  }

  async confirmAccounts(state: string, customerIds: string[]): Promise<void> {
    await apiRequest.post<Envelope<unknown>>('/google-ads/oauth/confirm', {
      state,
      customer_ids: customerIds,
    });
  }

  async disconnect(accountId: number): Promise<void> {
    await apiRequest.delete<Envelope<unknown>>(`/google-ads/connection/${accountId}`);
  }

  async getDashboard(params: {
    accountId?: number;
    preset: GoogleAdsDatePreset;
    startDate?: string;
    endDate?: string;
    level: GoogleAdsReportLevel;
    refresh?: boolean;
    includeDaily?: boolean;
    includeComparison?: boolean;
  }): Promise<GoogleAdsDashboard> {
    const response = await apiRequest.get<Envelope<GoogleAdsDashboard>>('/google-ads/dashboard', {
      params: {
        account_id: params.accountId,
        preset: params.preset,
        start_date: params.startDate,
        end_date: params.endDate,
        level: params.level,
        refresh: params.refresh ? 1 : 0,
        // 1回ずつ追加の GAQL 呼び出しになるため、要求したときだけ送る。
        include_daily: params.includeDaily ? 1 : 0,
        include_comparison: params.includeComparison ? 1 : 0,
      },
    });
    return response.data;
  }

  async createChange(input: GoogleAdsChangeInput): Promise<GoogleAdsChangeRequest> {
    const response = await apiRequest.post<Envelope<GoogleAdsChangeRequest>>(
      '/google-ads/change-requests',
      input,
    );
    return response.data;
  }

  async validateChange(id: string): Promise<{ valid: boolean; request_id: string | null }> {
    const response = await apiRequest.post<Envelope<{ valid: boolean; request_id: string | null }>>(
      `/google-ads/change-requests/${id}/validate`,
    );
    return response.data;
  }

  async executeChange(id: string): Promise<GoogleAdsChangeRequest> {
    const response = await apiRequest.post<Envelope<GoogleAdsChangeRequest>>(
      `/google-ads/change-requests/${id}/execute`,
    );
    return response.data;
  }

  async getAudit(accountId?: number): Promise<GoogleAdsChangeRequest[]> {
    const response = await apiRequest.get<Envelope<GoogleAdsChangeRequest[]>>('/google-ads/audit', {
      params: accountId ? { account_id: accountId } : undefined,
    });
    return response.data;
  }
}

const googleAdsService = new GoogleAdsService();

export default googleAdsService;
